/**
 * Тренд переделывает своё видео — ОДНО правило для виджета сайта и карточки тренда в админке (12.10).
 *
 * МАСТЕР — shared/trend-video; этот файл копирует в frontend/src/trend-video и admin/src/trend-video скрипт
 * scripts/sync_prompt_vars.sh (CI ловит расхождение). Править только мастер. Модуль ничего не импортирует из приложений.
 * Сервер решает тем же: видео тренда кладёт в запрос api/v1/video._apply_card_reference, переделку (формат adaptive,
 * длина -1) включает адаптер Seedance, списание по длине видео — services/seedance_refs. Что сайт, админка и сервер
 * решают одинаково, проверяют сценарии shared/trend-video/cases.json: тест бэка гоняет их через сервер и адаптер,
 * тест сайта — через это правило и сборку запроса виджета.
 *
 * Seedance 2.5 с видео тренда в режиме «Референсы» видео ПЕРЕДЕЛЫВАЕТ: формат кадра и длина ролика по правилу модели
 * берутся с видео (KIE: «the output ratio and duration follow the input video»; 12.10 на проде: видео тренда 4:3,
 * 15,04 с → ролик 752×560, 15,07 с). Выбрать их нельзя, поэтому полей формата и длины нет ни в виджете, ни в карточке
 * тренда — на их месте описание. Выбор остаётся в режиме «Кадры», у Seedance 2.0, у тренда без видео, когда
 * пользователь убрал видео тренда из слота или положил своё (у своего видео свои правила — utils/referenceLimits).
 */

/** Что известно о тренде и что сделал пользователь. Админка знает только тренд: пользователь ничего не трогал. */
export interface TrendVideoState {
	/** Модель тренда: id или backendModel. */
	model?: string | null
	/** Режим Seedance: 'frames' | 'references'; пусто — «Кадры», как у виджета и админки. */
	mode?: string | null
	/** У тренда есть видео. */
	trendVideo: boolean
	/** Видео тренда скрыто от пользователя: убрать его нельзя, сервер подставит его сам. */
	videoHidden?: boolean
	/** Пользователь убрал видео тренда из слота. */
	videoRemoved?: boolean
	/** Пользователь положил своё видео — оно главнее видео тренда (сервер видео тренда тогда не подставляет). */
	ownVideo?: boolean
}

export function isSeedance25(model: string | null | undefined): boolean {
	const m = String(model ?? '').toLowerCase()
	return m.includes('seedance-2-5') || m.includes('seedance-2.5')
}

/** Формат и длина ролика — как у видео тренда, выбрать их нельзя. */
export function followsTrendVideo(s: TrendVideoState): boolean {
	if (!s.trendVideo || !isSeedance25(s.model)) return false
	if ((s.mode || 'frames') !== 'references') return false
	if (s.ownVideo) return false
	return !!s.videoHidden || !s.videoRemoved
}

/** Формат «почти как у модели»: 1206×748 — уже не ровно 16:9, такой пишем с «≈» (тот же допуск у журнала генерации). */
const RATIO_TOLERANCE = 0.02
/** Запас на упаковку кадров: 15,04 с — это 15 с (backend services/seedance_refs.DURATION_TOLERANCE). */
const SECONDS_TOLERANCE = 0.25

function ratioOf(name: string): number | null {
	const m = /^(\d+)\s*:\s*(\d+)$/.exec(name.trim())
	if (!m) return null
	const a = Number(m[1])
	const b = Number(m[2])
	return a > 0 && b > 0 ? a / b : null
}

/** Форматы Seedance 2.5 (код адаптера: Seedance2Adapter.get_capabilities, без adaptive) — тест бэка сверяет их с
 *  адаптером через cases.json. Свой список, а не форматы модели из настроек: там бывает общий запасной (3:2, без 21:9). */
export const SEEDANCE_25_RATIOS: readonly string[] = ['16:9', '4:3', '1:1', '3:4', '9:16', '21:9']
/** Пределы длительности Seedance 2.5 в секундах — по ним сервер считает списание по длине видео
 *  (seedance_refs.reference_seconds_for_billing); тест бэка сверяет их с адаптером через cases.json. */
export const SEEDANCE_25_DURATION: readonly [number, number] = [4, 30]

/** Формат кадра видео — ближайший из форматов Seedance 2.5 (adaptive и прочие не-пропорции пропускаются). exact=false —
 *  видео заметно отличается, показываем «≈». null — размер не знаем. */
export function trendVideoAspect(
	width: number | null | undefined,
	height: number | null | undefined,
	ratios: readonly string[] = SEEDANCE_25_RATIOS,
): { aspect: string; exact: boolean } | null {
	const w = Number(width)
	const h = Number(height)
	if (!(w > 0) || !(h > 0)) return null
	let best: { aspect: string; diff: number; ratio: number } | null = null
	for (const name of ratios) {
		const r = ratioOf(name)
		if (r == null) continue
		const diff = Math.abs(Math.log(w / h / r))
		if (!best || diff < best.diff) best = { aspect: name, diff, ratio: r }
	}
	if (!best) return null
	return { aspect: best.aspect, exact: Math.abs(w / h - best.ratio) <= RATIO_TOLERANCE * best.ratio }
}

/** Длина ролика по видео тренда в целых секундах в пределах модели — столько же спишет сервер
 *  (services/seedance_refs.reference_seconds_for_billing). null — длину не знаем. */
export function trendVideoSeconds(seconds: number | null | undefined, min: number, max: number): number | null {
	const s = Number(seconds)
	if (!Number.isFinite(s) || s <= 0) return null
	return Math.min(max, Math.max(min, Math.ceil(s - SECONDS_TOLERANCE)))
}

/** Подпись строки, что стоит вместо полей формата и длины. */
export const TREND_VIDEO_LABEL = 'Формат и длина'

/** «как у видео тренда: 16:9, 15 с». Чего не знаем — не пишем. */
export function trendVideoValue(aspect: { aspect: string; exact: boolean } | null | undefined, seconds: number | null | undefined): string {
	const parts: string[] = []
	if (aspect) parts.push(`${aspect.exact ? '' : '≈'}${aspect.aspect}`)
	if (seconds != null && seconds > 0) parts.push(`${seconds} с`)
	return `как у видео тренда${parts.length ? `: ${parts.join(', ')}` : ''}`
}

/** «Формат и длина — как у видео тренда: 16:9, 15 с». */
export function trendVideoLine(aspect: { aspect: string; exact: boolean } | null | undefined, seconds: number | null | undefined): string {
	return `${TREND_VIDEO_LABEL} — ${trendVideoValue(aspect, seconds)}`
}

/** Почему выбора нет и что делать — для карточки тренда в админке. */
export const TREND_VIDEO_WHY = 'Тренд переделывает это видео, поэтому выбрать их нельзя.'
export const TREND_VIDEO_OTHER = 'Нужен другой формат — загрузите видео этого формата.'
